import { useState, useRef, useCallback, useEffect } from 'react';
import maplibregl from 'maplibre-gl';
import { FA_ICONS } from '../utils/constants';
import { getPoiAddress } from '../utils/poiAddress';
import rvgPin from '../assets/icons/rvgPin.png';

const POI_LAYER_IDS = [
  'pois_important',
  'pois_amenity',
  'pois_shop_tourism_leisure',
  'pois_natural_manmade'
];

// The unified source-layer name in the vector tiles
const POI_SOURCE_LAYER = 'poi_curated';

// Additional source-layers that still exist alongside poi_curated
const POI_ADDITIONAL_SOURCE_LAYERS = ['pois_service', 'pois_food', 'pois_shop', 'pois_transport', 'pois_major', 'pois_other'];

/**
 * Custom hook for managing Points of Interest (POI) functionality
 * Fetches POIs directly from vector tiles
 */
export const usePOI = (mapRef, currentZoom, { onPoiClick, priorityMarkerRefs } = {}) => {
  const [openPoiCategory, setOpenPoiCategory] = useState(null);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [poiCounts, setPoiCounts] = useState({});
  const [showPoiSidebar, setShowPoiSidebar] = useState(false);
  const [sidebarPois, setSidebarPois] = useState([]);
  const [sidebarCategory, setSidebarCategory] = useState(null);
  const [mapReady, setMapReady] = useState(false);

  const poiMarkersRef = useRef([]);
  const poiPopupsRef = useRef([]);
  const cachedPOIsRef = useRef(new Map()); // Cache POIs by amenity type
  const declutterTimeoutRef = useRef(null);
  const liveUpdateIntervalRef = useRef(null);
  const initialLoadRetryRef = useRef(null);
  const selectedPoiMarkerRef = useRef(null);
  const lastSidebarAmenityKeyRef = useRef('');

  const POI_FADE_MS = 220;
  const POI_LIVE_UPDATE_INTERVAL_MS = 120;
  const POI_MIN_ZOOM = 14.5;
  const POI_INITIAL_RETRY_MS = 500;
  const POI_INITIAL_RETRY_LIMIT = 10;

  const normalizeAmenity = useCallback((amenity) => {
    if (!amenity) return amenity;
    if (amenity === 'dlv_facility' || amenity === 'facility') {
      return 'facility';
    }
    return amenity;
  }, []);

  const getAmenityFromProps = useCallback((props) => {
    let amenity = props.amenity || props.shop || props.tourism || props.leisure || props.historic;
    if (!amenity && props.class) {
      // poi_curated uses capitalized class values (e.g. "Restaurant") — normalize to lowercase
      amenity = props.class.toLowerCase().replace(/\s+/g, '_');
    }
    return normalizeAmenity(amenity);
  }, [normalizeAmenity]);

  // Wait for mapRef.current to be set (ref changes don't re-render)
  useEffect(() => {
    if (mapReady) return;
    if (mapRef.current) {
      setMapReady(true);
      return;
    }
    const intervalId = setInterval(() => {
      if (mapRef.current) {
        clearInterval(intervalId);
        setMapReady(true);
      }
    }, 200);
    return () => clearInterval(intervalId);
  }, [mapRef, mapReady]);

  // Declutter POI markers to prevent overlap (icon + label)
  const declutterMarkers = useCallback(() => {
    if (!mapRef.current || poiMarkersRef.current.length === 0) return;

    const map = mapRef.current;
    const markerData = [];

    // Calculate screen positions and full bounding boxes for all markers (icon + label)
    poiMarkersRef.current.forEach(marker => {
      const lngLat = marker.getLngLat();
      const point = map.project(lngLat);
      const element = marker.getElement();

      if (element) {
        const wasDisplayNone = element.style.display === 'none';
        if (wasDisplayNone) {
          element.style.display = '';
          element.style.visibility = 'hidden';
          element.style.opacity = '0';
        }
        // Get the full bounding rect of the marker container (includes icon + label)
        const containerRect = element.getBoundingClientRect();

        // Also check for the label element to get accurate width
        const labelEl = element.querySelector('.poi-label');
        const iconEl = element.querySelector('img') || element.querySelector('.poi-circle');

        let totalWidth = containerRect.width;
        let totalHeight = containerRect.height;
        let leftOffset = 0;

        // If label exists, calculate the full width including label on the left
        if (labelEl && iconEl) {
          const labelRect = labelEl.getBoundingClientRect();
          const iconRect = iconEl.getBoundingClientRect();
          // Label is on the left of icon, so total width = label width + gap + icon width
          totalWidth = labelRect.width + 8 + iconRect.width;
          totalHeight = Math.max(labelRect.height, iconRect.height);
          // The marker anchor is at the icon, so we need to offset left by label width + gap
          leftOffset = labelRect.width + 8;
        }

        markerData.push({
          marker,
          element,
          x: point.x,
          y: point.y,
          width: totalWidth || 150,
          height: totalHeight || 28,
          leftOffset: leftOffset,
          visible: true
        });
      }
    });

    // Sort by y position (top to bottom) so topmost markers take priority
    markerData.sort((a, b) => a.y - b.y);

    // First, hide POI markers that overlap with priority markers (route pins, RVG pin, search pin)
    // and native transport layers (metro stations, bus stops)
    {
      const priorityBounds = [];
      if (priorityMarkerRefs && priorityMarkerRefs.length > 0) {
        priorityMarkerRefs.forEach(ref => {
          if (!ref || !ref.current) return;
          // Handle both single marker refs and array refs (like routeMarkersRef)
          const markers = Array.isArray(ref.current) ? ref.current : [ref.current];
          markers.forEach(rm => {
            try {
              const rmEl = rm.getElement ? rm.getElement() : rm;
              if (!rmEl) return;
              const rmRect = rmEl.getBoundingClientRect();
              if (rmRect.width === 0 && rmRect.height === 0) return;
              priorityBounds.push({
                left: rmRect.left,
                right: rmRect.right,
                top: rmRect.top,
                bottom: rmRect.bottom
              });
            } catch (e) {
              // Ignore
            }
          });
        });
      }

      // Also treat bus stops and metro stations as priority — hide POI markers that overlap them
      const transportLayers = ['bus_stops', 'metro_stations'];
      const mapContainer = map.getContainer();
      const mapRect = mapContainer.getBoundingClientRect();
      transportLayers.forEach(layerId => {
        try {
          if (!map.getLayer(layerId)) return;
          const features = map.queryRenderedFeatures(undefined, { layers: [layerId] });
          features.forEach(feature => {
            if (!feature.geometry || feature.geometry.type !== 'Point') return;
            const coords = feature.geometry.coordinates;
            const screenPoint = map.project(coords);
            // Convert map-relative coordinates to page-relative (matching getBoundingClientRect)
            const pageX = screenPoint.x + mapRect.left;
            const pageY = screenPoint.y + mapRect.top;
            // 14px icon size — create a bounding box around the screen point
            const halfSize = 10;
            priorityBounds.push({
              left: pageX - halfSize,
              right: pageX + halfSize,
              top: pageY - halfSize,
              bottom: pageY + halfSize
            });
          });
        } catch (e) {
          // Ignore — layer may not exist yet
        }
      });

      // Also treat place name labels as priority — hide POI markers that overlap them
      const placeLabelLayers = ['place_hamlet', 'place_village', 'place_town', 'place_city', 'place_state'];
      placeLabelLayers.forEach(layerId => {
        try {
          if (!map.getLayer(layerId)) return;
          const features = map.queryRenderedFeatures(undefined, { layers: [layerId] });
          features.forEach(feature => {
            if (!feature.geometry || feature.geometry.type !== 'Point') return;
            const coords = feature.geometry.coordinates;
            const screenPoint = map.project(coords);
            const pageX = screenPoint.x + mapRect.left;
            const pageY = screenPoint.y + mapRect.top;
            // Approximate text label bounds — labels are wider than icons
            const name = feature.properties?.name || '';
            const approxWidth = Math.max(name.length * 7, 60);
            const halfWidth = approxWidth / 2;
            const halfHeight = 12;
            priorityBounds.push({
              left: pageX - halfWidth,
              right: pageX + halfWidth,
              top: pageY - halfHeight,
              bottom: pageY + halfHeight
            });
          });
        } catch (e) {
          // Ignore — layer may not exist yet
        }
      });

      if (priorityBounds.length > 0) {
        const routePadding = 5;
        markerData.forEach(data => {
          if (!data.visible) return;
          const el = data.element;
          const poiRect = el.getBoundingClientRect();
          for (const rb of priorityBounds) {
            const overlapX = poiRect.left - routePadding < rb.right && poiRect.right + routePadding > rb.left;
            const overlapY = poiRect.top - routePadding < rb.bottom && poiRect.bottom + routePadding > rb.top;
            if (overlapX && overlapY) {
              data.visible = false;
              break;
            }
          }
        });
      }
    }

    // Check for overlaps and hide overlapping markers
    for (let i = 0; i < markerData.length; i++) {
      if (!markerData[i].visible) continue;

      for (let j = i + 1; j < markerData.length; j++) {
        if (!markerData[j].visible) continue;

        const a = markerData[i];
        const b = markerData[j];

        // Calculate actual bounding boxes considering label offset
        // Marker A bounds (label is on left of icon)
        const aLeft = a.x - a.leftOffset;
        const aRight = a.x + (a.width - a.leftOffset);
        const aTop = a.y - a.height / 2;
        const aBottom = a.y + a.height / 2;

        // Marker B bounds
        const bLeft = b.x - b.leftOffset;
        const bRight = b.x + (b.width - b.leftOffset);
        const bTop = b.y - b.height / 2;
        const bBottom = b.y + b.height / 2;

        // Check for overlap with padding
        const padding = 7;
        const overlapX = aLeft - padding < bRight && aRight + padding > bLeft;
        const overlapY = aTop - padding < bBottom && aBottom + padding > bTop;

        if (overlapX && overlapY) {
          // Hide the marker that's lower (higher y value)
          markerData[j].visible = false;
        }
      }
    }
    markerData.forEach(data => {
      if (data.visible) {
        data.element.style.display = '';
        data.element.style.visibility = 'visible';
        data.element.style.opacity = '1';
      } else {
        data.element.style.display = 'none';
      }
    });

    // Update the POI collision placeholder source so MapLibre hides
    // road labels (text-optional:true) that overlap with visible POI markers
    try {
      const collisionSource = map.getSource('poi-collision-placeholder');
      if (collisionSource) {
        const features = markerData
          .filter(d => d.visible)
          .map(d => ({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [d.marker.getLngLat().lng, d.marker.getLngLat().lat]
            },
            properties: { w: d.width || 120 }
          }));
        collisionSource.setData({
          type: 'FeatureCollection',
          features
        });
      }
    } catch (e) {
      // Collision placeholder update failed silently
    }
  }, [mapRef, priorityMarkerRefs]);

  // Debounced declutter function for performance
  const debouncedDeclutter = useCallback(() => {
    if (declutterTimeoutRef.current) {
      clearTimeout(declutterTimeoutRef.current);
    }
    declutterTimeoutRef.current = setTimeout(() => {
      declutterMarkers();
    }, 100);
  }, [declutterMarkers]);

  // Toggle amenity selection
  const toggleAmenity = useCallback((amenity) => {
    setSelectedAmenities(prev => {
      if (prev.includes(amenity)) {
        return prev.filter(a => a !== amenity);
      } else {
        return [...prev, amenity];
      }
    });
  }, []);

  // Select only amenities from a specific category (deselect all others)
  const selectOnlyCategory = useCallback((categoryAmenities) => {
    setSelectedAmenities(categoryAmenities);
  }, []);

  const resetSelectedPoiMarker = useCallback(() => {
    const selected = selectedPoiMarkerRef.current;
    if (!selected) return;
    const { marker, originalIconHTML } = selected;
    const element = marker?.getElement?.();
    if (element) {
      element.classList.remove('poi-selected');
      const iconWrapper = element.querySelector('.poi-icon-wrapper');
      if (iconWrapper && originalIconHTML) {
        iconWrapper.innerHTML = originalIconHTML;
      }
    }
    selectedPoiMarkerRef.current = null;
  }, []);

  // Clear POI markers from map
  const clearPoiLayers = useCallback(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    // Close all popups
    poiPopupsRef.current.forEach(popup => {
      try {
        popup.remove();
      } catch (e) {
        // Ignore
      }
    });
    poiPopupsRef.current = [];

    resetSelectedPoiMarker();

    // Remove HTML markers
    poiMarkersRef.current.forEach(marker => marker.remove());
    poiMarkersRef.current = [];

    // Clear cache when clearing POIs
    cachedPOIsRef.current.clear();

    // Clear collision placeholder so road labels reappear
    try {
      const collisionSource = map.getSource('poi-collision-placeholder');
      if (collisionSource) {
        collisionSource.setData({ type: 'FeatureCollection', features: [] });
      }
    } catch (e) {
      // Ignore
    }
  }, [mapRef, resetSelectedPoiMarker]);

  // Fetch and render POIs from vector tiles
  const updatePOIs = useCallback(async (options = {}) => {
    if (!mapRef.current) {
      clearPoiLayers();
      return;
    }

    const map = mapRef.current;
    const { waitForIdle = true, liveMode = false } = options;
    const shouldLiveQuery = liveMode || !waitForIdle;
    const zoom = map.getZoom();

    const fadeOutAndRemove = (marker) => {
      if (selectedPoiMarkerRef.current?.marker === marker) {
        resetSelectedPoiMarker();
      }
      try {
        const el = marker.getElement();
        if (el) {
          el.classList.add('poi-fade-out');
        }
      } catch (e) {
        // Ignore
      }
      setTimeout(() => {
        try {
          marker.remove();
        } catch (e) {
          // Ignore
        }
      }, POI_FADE_MS);
    };

    // Show POIs only after a minimum zoom level
    // EXCEPTION: If user has selected a POI category, keep showing POIs even below the minimum
    if (zoom <= POI_MIN_ZOOM && selectedAmenities.length === 0) {
      // Fade out existing markers smoothly when zooming out
      poiMarkersRef.current.forEach(marker => fadeOutAndRemove(marker));
      poiMarkersRef.current = [];
      cachedPOIsRef.current.clear();

      // Close all popups immediately
      poiPopupsRef.current.forEach(popup => {
        try {
          popup.remove();
        } catch (e) {
          // Ignore
        }
      });
      poiPopupsRef.current = [];

      // Clear collision placeholder so city/town labels reappear
      try {
        const collisionSource = map.getSource('poi-collision-placeholder');
        if (collisionSource) {
          collisionSource.setData({ type: 'FeatureCollection', features: [] });
        }
      } catch (e) {
        // Ignore
      }
      return;
    }

    // Check if style is loaded
    if (!map.isStyleLoaded()) {
      return;
    }

    const collectFeatures = (useRendered = false) => {
      try {
        const allFeatures = [];

        // Query features from vector tile source to get POIs
        const bounds = map.getBounds();

        // querySourceFeatures queries the raw vector tile data directly.
        // Primary POIs are in "poi_curated", with additional layers for specific types.
        const sourceLayersToQuery = [POI_SOURCE_LAYER, ...POI_ADDITIONAL_SOURCE_LAYERS];

        sourceLayersToQuery.forEach(sourceLayer => {
          try {
            const features = map.querySourceFeatures('tegola', {
              sourceLayer: sourceLayer
            });

            // Filter features to only those in current viewport
            const visibleFeatures = features.filter(feature => {
              if (feature.geometry && feature.geometry.type === 'Point') {
                const coords = feature.geometry.coordinates;
                return bounds.contains(coords);
              }
              return true;
            });

            allFeatures.push(...visibleFeatures);
          } catch (err) {
          }
        });

        // Fallback: if querySourceFeatures returned nothing, try queryRenderedFeatures
        if (allFeatures.length === 0 && useRendered) {
          const rendered = map.queryRenderedFeatures(undefined, { layers: POI_LAYER_IDS });
          rendered.forEach(feature => {
            if (feature.geometry && feature.geometry.type === 'Point') {
              allFeatures.push(feature);
            }
          });
        }

        return allFeatures;
      } catch (error) {
        return [];
      }
    };

    // Wait for map to be idle (fully rendered) before querying features
    const queryFeatures = () => {
      return new Promise((resolve) => {
        const doQuery = () => {
          const allFeatures = collectFeatures(shouldLiveQuery);
          resolve(allFeatures);
        };

        // Check if source data is loaded
        const isSourceLoaded = () => {
          const source = map.getSource('tegola');
          if (!source) return false;

          // Check if tiles are loaded by looking at the source cache
          // This is a heuristic - if the source exists and map is idle, tiles should be loaded
          return map.areTilesLoaded && map.areTilesLoaded();
        };

        // If we're in live update mode, query immediately and avoid waiting for idle
        if (shouldLiveQuery) {
          map.triggerRepaint();
          doQuery();
          return;
        }

        // Force map to render tiles first
        map.triggerRepaint();

        // Set a timeout fallback in case events never fire
        const timeoutId = setTimeout(() => {
          doQuery();
        }, 1000); // 1 second fallback

        // Wait for both idle AND sourcedata events to ensure tiles are loaded
        let idleFired = false;
        let sourcedataFired = false;

        const checkAndQuery = () => {
          if (idleFired && sourcedataFired) {
            clearTimeout(timeoutId);
            // Use double requestAnimationFrame to ensure rendering is complete
            requestAnimationFrame(() => {
              requestAnimationFrame(doQuery);
            });
          }
        };

        const onIdle = () => {
          idleFired = true;
          checkAndQuery();
        };

        const onSourceData = (e) => {
          // Only care about the tegola source
          if (e.sourceId === 'tegola' && e.isSourceLoaded) {
            sourcedataFired = true;
            map.off('sourcedata', onSourceData);
            checkAndQuery();
          }
        };

        // Listen for both events
        map.once('idle', onIdle);
        map.on('sourcedata', onSourceData);

        // If source is already loaded, mark it as fired
        if (isSourceLoaded()) {
          sourcedataFired = true;
          checkAndQuery();
        }
      });
    };

    try {
      const allFeatures = shouldLiveQuery ? collectFeatures(true) : await queryFeatures();
      const zoom = map.getZoom();

      if (liveMode && allFeatures.length === 0) {
        // Avoid clearing during live updates if tiles aren't ready yet
        return;
      }

      // Filter by selected amenities and remove duplicates
      const seenIds = new Set();
      const newFeatures = allFeatures.filter(feature => {
        const props = feature.properties || {};
        const amenity = getAmenityFromProps(props);



        const id = feature.id || `${feature.geometry.coordinates[0]}_${feature.geometry.coordinates[1]}`;

        // Show all POIs with amenity (no selection filter)
        if (amenity && !seenIds.has(id)) {
          seenIds.add(id);
          return true;
        }
        return false;
      });

      // Add new features to cache
      newFeatures.forEach(feature => {
        const props = feature.properties || {};
        let amenity = props.amenity || props.shop || props.tourism || props.leisure || props.historic;
        if (!amenity && props.class) {
          amenity = props.class.toLowerCase().replace(/\s+/g, '_');
        }
        amenity = normalizeAmenity(amenity);

        const id = feature.id || `${feature.geometry.coordinates[0]}_${feature.geometry.coordinates[1]}`;

        if (!cachedPOIsRef.current.has(id)) {
          cachedPOIsRef.current.set(id, feature);
        }
      });

      // Get all cached POIs
      const allCachedFeatures = Array.from(cachedPOIsRef.current.values()).filter(feature => {
        const props = feature.properties || {};
        let amenity = props.amenity || props.shop || props.tourism || props.leisure || props.historic;
        if (!amenity && props.class) {
          amenity = props.class.toLowerCase().replace(/\s+/g, '_');
        }
        const normalizedAmenity = normalizeAmenity(amenity);

        // If no amenities selected, show all POIs EXCEPT Delhivery facilities
        if (selectedAmenities.length === 0) {
          return normalizedAmenity && normalizedAmenity !== 'facility';
        }

        // If amenities are selected, only show those
        return normalizedAmenity && selectedAmenities.includes(normalizedAmenity);
      });

      // Get current map bounds to filter POIs within viewport
      const bounds = map.getBounds();
      const mapCenter = map.getCenter();

      // Filter POIs to only those within current viewport
      // EXCEPTION: If user has selected POI categories, don't filter by viewport - keep all loaded POIs
      const poisInViewport = selectedAmenities.length > 0
        ? allCachedFeatures // Keep all loaded POIs when categories are selected
        : allCachedFeatures.filter(feature => { // Normal behavior: filter by viewport
          const coords = feature.geometry.coordinates;
          return bounds.contains(coords);
        });

      // Define category mappings
      const categoryMap = {
        'restaurant': 'FOOD',
        'cafe': 'FOOD',
        'fast_food': 'FOOD',
        'hospital': 'HEALTH',
        'clinic': 'HEALTH',
        'pharmacy': 'HEALTH',
        'school': 'EDUCATION',
        'college': 'EDUCATION',
        'university': 'EDUCATION',
        'kindergarten': 'EDUCATION',
        'library': 'EDUCATION',
        'bank': 'FINANCE',
        'atm': 'FINANCE',
        'marketplace': 'SHOPPING',
        'mall': 'SHOPPING',
        'supermarket': 'SHOPPING',
        'convenience': 'SHOPPING',
        'fuel': 'SERVICES',
        'police': 'SERVICES',
        'post_office': 'SERVICES',
        'toll': 'SERVICES',
        'apartment': 'APARTMENT',
        'facility': 'FACILITY',
        'hotel': 'HOTEL',
        'temple': 'TEMPLE',
        'church': 'CHURCH',
        'gurudwara': 'GURUDWARA',
        'mosque': 'MOSQUE',
        'theatre': 'THEATRE',
        'castle': 'CASTLE',
        'attraction': 'ATTRACTION'
      };

      // Helper function to calculate distance from map center
      const getDistance = (coords) => {
        const dx = coords[0] - mapCenter.lng;
        const dy = coords[1] - mapCenter.lat;
        return Math.sqrt(dx * dx + dy * dy);
      };

      // Group POIs by category and sort by distance from center
      const poisByCategory = {};
      poisInViewport.forEach(feature => {
        const props = feature.properties || {};
        let amenity = props.amenity || props.shop || props.tourism || props.leisure || props.historic;
        if (!amenity && props.class) {
          amenity = props.class.toLowerCase().replace(/\s+/g, '_');
        }
        amenity = normalizeAmenity(amenity);

        const category = categoryMap[amenity];
        if (category) {
          if (!poisByCategory[category]) {
            poisByCategory[category] = [];
          }
          // Add distance property for sorting
          feature._distance = getDistance(feature.geometry.coordinates);
          poisByCategory[category].push(feature);
        }
      });

      // Sort each category by distance and limit based on zoom level
      // Zoom > 18.5: show ALL POIs (no limit)
      // Zoom 17-18.5: show 20 POIs per category
      // Zoom 16-17: show 12 POIs per category
      // Zoom 15-16: show 8 POIs per category
      // Zoom 14.5-15: show 6 POIs per category
      const poisPerCategory = zoom > 18.5 ? Infinity : zoom > 17 ? 20 : zoom > 16 ? 12 : zoom > 15 ? 8 : 6;

      const filteredFeatures = [];
      Object.keys(poisByCategory).forEach(category => {
        const sortedPois = poisByCategory[category].sort((a, b) => a._distance - b._distance);
        const categoryPois = sortedPois.slice(0, poisPerCategory);
        filteredFeatures.push(...categoryPois);
      });

      // Count by type (use all filtered, not limited)
      const typeCounts = {};
      filteredFeatures.forEach(feature => {
        const props = feature.properties || {};
        // Extract amenity using same logic as filtering
        let amenity = props.amenity || props.shop || props.tourism || props.leisure || props.historic;
        if (!amenity && props.class) {
          amenity = props.class.toLowerCase().replace(/\s+/g, '_');
        }
        amenity = normalizeAmenity(amenity);

        if (amenity) {
          typeCounts[amenity] = (typeCounts[amenity] || 0) + 1;
        }
      });
      setPoiCounts(typeCounts);

      const getPoiId = (feature) =>
        feature?.id || `${feature?.geometry?.coordinates?.[0]}_${feature?.geometry?.coordinates?.[1]}`;

      const dedupeFeatures = (features) => {
        const seen = new Set();
        const deduped = [];
        features.forEach((feature) => {
          const id = getPoiId(feature);
          if (!id || seen.has(id)) return;
          seen.add(id);
          deduped.push(feature);
        });
        return deduped;
      };

      const selectedAmenitiesKey = [...selectedAmenities].sort().join('|');

      // Always update sidebar POIs when amenities are selected.
      // Keep existing order and append new POIs to the end (no reordering).
      if (selectedAmenities.length > 0) {
        if (selectedAmenitiesKey !== lastSidebarAmenityKeyRef.current) {
          lastSidebarAmenityKeyRef.current = selectedAmenitiesKey;
          setSidebarPois(dedupeFeatures(filteredFeatures));
        } else {
          setSidebarPois((prev) => {
            const prevIds = new Set(prev.map(getPoiId));
            const next = [...prev];
            filteredFeatures.forEach((feature) => {
              const id = getPoiId(feature);
              if (!id || prevIds.has(id)) return;
              prevIds.add(id);
              next.push(feature);
            });
            return next;
          });
        }
      } else {
        lastSidebarAmenityKeyRef.current = '';
        setSidebarPois([]);
      }

      // Don't clear all markers! Only remove markers that are no longer in selected amenities
      // EXCEPTION: If categories are selected, keep ALL markers from selected categories
      if (!liveMode && selectedAmenities.length === 0) {
        // Normal behavior: remove markers not in current filtered set
        const shouldDisplayIds = new Set(
          filteredFeatures.map(f => f.id || `${f.geometry.coordinates[0]}_${f.geometry.coordinates[1]}`)
        );

        poiMarkersRef.current = poiMarkersRef.current.filter(marker => {
          const shouldKeep = marker._poiId && shouldDisplayIds.has(marker._poiId);
          if (!shouldKeep) {
            fadeOutAndRemove(marker);
          }
          return shouldKeep;
        });
      } else if (!liveMode) {
        // Categories are selected: keep all markers from selected categories, remove others
        poiMarkersRef.current = poiMarkersRef.current.filter(marker => {
          // Check if this marker's amenity is in the selected list
          const markerAmenity = marker._amenity;
          const shouldKeep = markerAmenity && selectedAmenities.includes(markerAmenity);

          if (!shouldKeep) {
            fadeOutAndRemove(marker);
          }
          return shouldKeep;
        });
      }

      // Close all open POI popups when zooming
      const closeAllPopups = () => {
        poiPopupsRef.current.forEach(popup => {
          try {
            popup.remove();
          } catch (e) {
            // Popup might already be removed
          }
        });
        poiPopupsRef.current = [];
      };

      // Add zoom listener to close popups (only once)
      map.off('zoom', closeAllPopups);
      map.on('zoom', closeAllPopups);

      // Use requestAnimationFrame to batch DOM updates
      requestAnimationFrame(() => {
        // Get existing marker IDs to avoid duplicates
        const existingMarkerIds = new Set(poiMarkersRef.current.map(m => m._poiId).filter(Boolean));

        // Render ALL cached POIs (no limit) - only add new ones
        filteredFeatures.forEach(feature => {
          const id = feature.id || `${feature.geometry.coordinates[0]}_${feature.geometry.coordinates[1]}`;

          // Skip if marker already exists
          if (existingMarkerIds.has(id)) {
            return;
          }
          const coords = feature.geometry.coordinates;
          const [lon, lat] = coords;
          const props = feature.properties || {};

          // Get amenity type using same extraction logic
          let amenity = props.amenity || props.shop || props.tourism || props.leisure || props.historic;
          if (!amenity && props.class) {
            amenity = props.class.toLowerCase().replace(/\s+/g, '_');
          }
          amenity = normalizeAmenity(amenity) || 'default';

          // List of amenities we have icons for
          const supportedAmenities = [
            'hospital', 'clinic', 'pharmacy', 'doctors', 'dentist', 'nursing_home',
            'school', 'college', 'university', 'kindergarten', 'library',
            'restaurant', 'cafe', 'fast_food',
            'bank', 'atm',
            'marketplace', 'mall', 'supermarket', 'convenience',
            'fuel', 'police', 'post_office', 'toll',
            'facility',
            'hotel',
            'temple',
            'church',
            'gurudwara',
            'mosque',
            'theatre',
            'castle',
            'attraction',
            'apartment'
          ];

          // Skip POIs we don't have icons for or potholes
          if (!supportedAmenities.includes(amenity) || amenity === 'pothole') {
            return;
          }

          const name = props.name || amenity.charAt(0).toUpperCase() + amenity.slice(1);
          const faIcon = FA_ICONS[amenity] || FA_ICONS.default;

          // Capitalize first letter of each word, rest lowercase
          const toTitleCase = (str) => {
            return str.replace(/\b\w/g, (char) => char.toUpperCase());
          };

          // Function to wrap text after the second word with 26 character limit
          const formatPOIText = (text) => {
            // Title case: first letter of each word uppercase
            let titleCased = toTitleCase(text);
            // Limit to 26 characters — truncate at word boundary
            let limitedText = titleCased;
            if (titleCased.length > 26) {
              limitedText = titleCased.substring(0, 26);
              // Cut back to last complete word
              const lastSpace = limitedText.lastIndexOf(' ');
              if (lastSpace > 10) {
                limitedText = limitedText.substring(0, lastSpace);
              }
              limitedText += '...';
            }

            // Split into words
            const words = limitedText.split(' ');

            // If we have more than 2 words, wrap after the second word
            if (words.length > 2) {
              const firstLine = words.slice(0, 2).join(' ');
              const secondLine = words.slice(2).join(' ');
              return firstLine + '<br>' + secondLine;
            }

            return limitedText;
          };

          const wrappedName = formatPOIText(name);

          // Skip if invalid coordinates
          if (!lon || !lat || isNaN(lon) || isNaN(lat)) {
            return;
          }

          // Create custom POI marker element with label on the right
          const el = document.createElement('div');
          el.className = 'poi-marker-container poi-fade-in-start';
          el.style.cursor = 'pointer';
          // Keep marker hidden until declutter completes to avoid flicker
          el.style.visibility = 'hidden';
          el.style.opacity = '0';

          // Use amenity for class
          const poiClass = `poi-${amenity}`;

          // Use custom PNG icons for health, education, food, finance, shopping, services, facilities amenities (no background/border), Font Awesome for others
          const customIcons = {
            // Health
            hospital: '/icons/hospital_poi_icon.png',
            clinic: '/icons/clinic_poi_icon.png',
            pharmacy: '/icons/pharmacies_poi_icon.png',
            // Education
            school: '/icons/school_poi_icon.png',
            college: '/icons/college_poi_icon.png',
            university: '/icons/college_poi_icon.png',
            kindergarten: '/icons/college_poi_icon.png',
            library: '/icons/library_poi_icon.png',
            // Food
            restaurant: '/icons/restuarant_poi_icon.png',
            cafe: '/icons/cafe_poi_icon.png',
            fast_food: '/icons/fast_food_poi.png',
            // Finance
            bank: '/icons/bank_poi_icon.png',
            atm: '/icons/atm_icon.png',
            // Shopping
            marketplace: '/icons/supermarket_poi.png',
            mall: '/icons/shopping_mall_poi.png',
            supermarket: '/icons/supermarket_poi.png',
            convenience: '/icons/shopping_mall_poi.png',
            // Services
            fuel: '/icons/fuel_staion_icon.png',
            police: '/icons/police_poi_icon.png',
            post_office: '/icons/post_office_poi_icon.png',
            toll: '/icons/_v1Toll Plaza.png',
            // Facilities
            facility: '/icons/_v1DlvFacility.png',
            // Hotels
            hotel: '/icons/hotel_poi_icon.png',
            // Temples
            temple: '/icons/temple_poi_icon.png',
            // Churches
            church: '/icons/church_poi_icon.png',
            // Gurudwaras
            gurudwara: '/icons/gurudwara_poi_icon.png',
            // Mosques
            mosque: '/icons/poi_icon_mosque.png',
            // Theatre
            theatre: '/icons/theatre_poi_icon.png',
            // Forts
            castle: '/icons/fort_icon_poi.png',
            // Tourism
            attraction: '/icons/tourism_poi_icon.png',
            // Apartments
            apartment: '/icons/apartment_poi_icon.png'
          };

          if (customIcons[amenity]) {
            // Get label color class based on category
            let labelColorClass = 'poi-label-default';
            if (['hospital', 'clinic', 'pharmacy'].includes(amenity)) {
              labelColorClass = 'poi-label-health';
            } else if (['school', 'college', 'university', 'kindergarten', 'library'].includes(amenity)) {
              labelColorClass = 'poi-label-education';
            } else if (['bank', 'atm'].includes(amenity)) {
              labelColorClass = 'poi-label-finance';
            } else if (['marketplace', 'mall', 'supermarket', 'convenience'].includes(amenity)) {
              labelColorClass = 'poi-label-shopping';

            } else if (amenity === 'restaurant') {
              labelColorClass = 'poi-label-restaurant';
            } else if (['cafe', 'fast_food'].includes(amenity)) {
              labelColorClass = 'poi-label-cafe';
            } else if (amenity === 'fuel') {
              labelColorClass = 'poi-label-fuel';
            } else if (amenity === 'police') {
              labelColorClass = 'poi-label-police';
            } else if (amenity === 'post_office') {
              labelColorClass = 'poi-label-post';
            } else if (amenity === 'apartment') {
              labelColorClass = 'poi-label-apartment';
            } else if (amenity === 'hotel') {
              labelColorClass = 'poi-label-hotel';
            } else if (amenity === 'temple') {
              labelColorClass = 'poi-label-temple';
            } else if (amenity === 'church') {
              labelColorClass = 'poi-label-church';
            } else if (amenity === 'gurudwara') {
              labelColorClass = 'poi-label-gurudwara';
            } else if (amenity === 'mosque') {
              labelColorClass = 'poi-label-mosque';
            } else if (amenity === 'theatre') {
              labelColorClass = 'poi-label-theatre';
            } else if (amenity === 'castle') {
              labelColorClass = 'poi-label-castle';
            } else if (amenity === 'attraction') {
              labelColorClass = 'poi-label-attraction';
            }

            el.innerHTML = `
            <div class="poi-inner-wrapper">
              <div class="poi-icon-wrapper">
                <img src="${customIcons[amenity]}" alt="${amenity}" class="poi-icon-img" />
              </div>
              <div class="poi-label ${labelColorClass}">${wrappedName}</div>
            </div>
          `;
          } else {
            // Get label color class based on category
            let labelColorClass = 'poi-label-default';
            if (['hospital', 'clinic', 'pharmacy'].includes(amenity)) {
              labelColorClass = 'poi-label-health';
            } else if (['school', 'college', 'university', 'kindergarten', 'library'].includes(amenity)) {
              labelColorClass = 'poi-label-education';
            } else if (['bank', 'atm'].includes(amenity)) {
              labelColorClass = 'poi-label-finance';
            } else if (['marketplace', 'mall', 'supermarket', 'convenience'].includes(amenity)) {
              labelColorClass = 'poi-label-shopping';

            } else if (amenity === 'restaurant') {
              labelColorClass = 'poi-label-restaurant';
            } else if (['cafe', 'fast_food'].includes(amenity)) {
              labelColorClass = 'poi-label-cafe';
            } else if (amenity === 'fuel') {
              labelColorClass = 'poi-label-fuel';
            } else if (amenity === 'police') {
              labelColorClass = 'poi-label-police';
            } else if (amenity === 'post_office') {
              labelColorClass = 'poi-label-post';
            } else if (amenity === 'apartment') {
              labelColorClass = 'poi-label-apartment';
            } else if (amenity === 'hotel') {
              labelColorClass = 'poi-label-hotel';
            } else if (amenity === 'temple') {
              labelColorClass = 'poi-label-temple';
            } else if (amenity === 'church') {
              labelColorClass = 'poi-label-church';
            } else if (amenity === 'gurudwara') {
              labelColorClass = 'poi-label-gurudwara';
            } else if (amenity === 'mosque') {
              labelColorClass = 'poi-label-mosque';
            } else if (amenity === 'theatre') {
              labelColorClass = 'poi-label-theatre';
            } else if (amenity === 'castle') {
              labelColorClass = 'poi-label-castle';
            } else if (amenity === 'attraction') {
              labelColorClass = 'poi-label-attraction';
            }

            el.innerHTML = `
            <div class="poi-inner-wrapper">
              <div class="poi-icon-wrapper">
                <div class="poi-circle poi-circle-fa ${poiClass}">
                  <i class="${faIcon}"></i>
                </div>
              </div>
              <div class="poi-label poi-label-fa ${labelColorClass}">${wrappedName}</div>
            </div>
          `;
          }
          el.title = name;

          const iconWrapper = el.querySelector('.poi-icon-wrapper');
          const originalIconHTML = iconWrapper ? iconWrapper.innerHTML : '';

          const marker = new maplibregl.Marker({
            element: el,
            anchor: 'center', // Center anchor keeps icon on exact coordinates
            pitchAlignment: 'map',
            rotationAlignment: 'map'
          })
            .setLngLat([lon, lat])
            .addTo(map);

          // Set high z-index on the marker element to ensure it appears above map labels
          const markerElement = marker.getElement();
          if (markerElement) {
            markerElement.style.zIndex = '1000';
          }

          // Store POI ID and amenity type on marker for tracking
          marker._poiId = id;
          marker._amenity = amenity;
          marker._coords = [lon, lat];
          marker._originalIconHTML = originalIconHTML;
          poiMarkersRef.current.push(marker);

          // Click handler - open bottom card and highlight clicked marker
          el.addEventListener('click', (e) => {
            e.stopPropagation();

            if (selectedPoiMarkerRef.current?.marker !== marker) {
              resetSelectedPoiMarker();
            }

            if (iconWrapper) {
              iconWrapper.innerHTML = '';
              const selectedImg = document.createElement('img');
              selectedImg.src = rvgPin;
              selectedImg.alt = 'Selected POI';
              selectedImg.className = 'poi-icon-img poi-selected-icon';
              iconWrapper.appendChild(selectedImg);
            }

            el.classList.add('poi-selected');
            selectedPoiMarkerRef.current = { marker, originalIconHTML };

            if (typeof onPoiClick === 'function') {
              const derivedAddress = getPoiAddress(props);
              onPoiClick({
                name,
                amenity,
                address: derivedAddress || '',
                lat,
                lon
              });
            }
          });

          // Fade in on next frame for smoothness
          requestAnimationFrame(() => {
            el.classList.remove('poi-fade-in-start');
            el.classList.add('poi-fade-in');
          });
        });

        // Declutter markers after all are added
        requestAnimationFrame(() => {
          declutterMarkers();
        });
      });

    } catch (error) {
      // Error fetching POIs
    }
  }, [mapRef, selectedAmenities, clearPoiLayers, declutterMarkers, getAmenityFromProps, normalizeAmenity, onPoiClick, resetSelectedPoiMarker]);

  // Update POIs when amenities change or map moves - visible at all zoom levels
  useEffect(() => {
    if (mapReady && mapRef.current) {
      // Always call updatePOIs - it will handle zoom logic internally
      updatePOIs({ waitForIdle: true });
    }
  }, [selectedAmenities, updatePOIs, mapRef, clearPoiLayers, mapReady]);

  // Ensure POIs load after map style is ready (e.g., after refresh)
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;

    const map = mapRef.current;
    let retryCount = 0;

    const handleStyleReady = () => {
      updatePOIs({ waitForIdle: true });

      if (initialLoadRetryRef.current) {
        clearInterval(initialLoadRetryRef.current);
        initialLoadRetryRef.current = null;
      }

      initialLoadRetryRef.current = setInterval(() => {
        retryCount += 1;
        if (poiMarkersRef.current.length > 0 || retryCount >= POI_INITIAL_RETRY_LIMIT) {
          clearInterval(initialLoadRetryRef.current);
          initialLoadRetryRef.current = null;
          return;
        }
        // Try a live update to catch tiles as they stream in
        updatePOIs({ waitForIdle: false, liveMode: true });
      }, POI_INITIAL_RETRY_MS);
    };

    const handleSourceData = (e) => {
      if (e.sourceId !== 'tegola') return;
      if (!e.isSourceLoaded && e.sourceDataType !== 'content') return;
      updatePOIs({ waitForIdle: false, liveMode: true });
    };

    map.on('load', handleStyleReady);
    map.on('style.load', handleStyleReady);
    map.on('sourcedata', handleSourceData);

    if (map.isStyleLoaded && map.isStyleLoaded()) {
      handleStyleReady();
    }

    return () => {
      if (initialLoadRetryRef.current) {
        clearInterval(initialLoadRetryRef.current);
        initialLoadRetryRef.current = null;
      }
      if (map) {
        map.off('load', handleStyleReady);
        map.off('style.load', handleStyleReady);
        map.off('sourcedata', handleSourceData);
      }
    };
  }, [mapRef, updatePOIs, mapReady]);

  // Continuous updates while interacting (drag/zoom/rotate)
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;

    const map = mapRef.current;

    const startLiveUpdates = () => {
      if (liveUpdateIntervalRef.current) return;
      updatePOIs({ waitForIdle: false, liveMode: true });
      liveUpdateIntervalRef.current = setInterval(() => {
        updatePOIs({ waitForIdle: false, liveMode: true });
      }, POI_LIVE_UPDATE_INTERVAL_MS);
    };

    const stopLiveUpdates = () => {
      if (liveUpdateIntervalRef.current) {
        clearInterval(liveUpdateIntervalRef.current);
        liveUpdateIntervalRef.current = null;
      }
      updatePOIs({ waitForIdle: true });
    };

    map.on('movestart', startLiveUpdates);
    map.on('zoomstart', startLiveUpdates);
    map.on('rotatestart', startLiveUpdates);

    map.on('moveend', stopLiveUpdates);
    map.on('zoomend', stopLiveUpdates);
    map.on('rotateend', stopLiveUpdates);

    return () => {
      if (liveUpdateIntervalRef.current) {
        clearInterval(liveUpdateIntervalRef.current);
        liveUpdateIntervalRef.current = null;
      }
      if (map) {
        map.off('movestart', startLiveUpdates);
        map.off('zoomstart', startLiveUpdates);
        map.off('rotatestart', startLiveUpdates);
        map.off('moveend', stopLiveUpdates);
        map.off('zoomend', stopLiveUpdates);
        map.off('rotateend', stopLiveUpdates);
      }
    };
  }, [mapRef, updatePOIs, mapReady]);

  // Declutter markers on map move, zoom, or pitch/rotate
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;

    const map = mapRef.current;

    // Attach declutter to map events
    map.on('move', debouncedDeclutter);
    map.on('zoom', debouncedDeclutter);
    map.on('pitch', debouncedDeclutter);
    map.on('rotate', debouncedDeclutter);

    return () => {
      if (map) {
        map.off('move', debouncedDeclutter);
        map.off('zoom', debouncedDeclutter);
        map.off('pitch', debouncedDeclutter);
        map.off('rotate', debouncedDeclutter);
      }
      if (declutterTimeoutRef.current) {
        clearTimeout(declutterTimeoutRef.current);
      }
    };
  }, [mapRef, debouncedDeclutter, mapReady]);

  return {
    openPoiCategory,
    setOpenPoiCategory,
    selectedAmenities,
    setSelectedAmenities,
    poiCounts,
    poiMarkersRef,
    poiPopupsRef,
    toggleAmenity,
    selectOnlyCategory,
    clearPoiLayers,
    updatePOIs,
    showPoiSidebar,
    setShowPoiSidebar,
    sidebarPois,
    setSidebarPois,
    sidebarCategory,
    setSidebarCategory,
    resetSelectedPoiMarker,
  };
};


